import { useEffect, useRef, useState } from 'react';
import { api, usd } from '../lib/api';
import { ErrorNote, Modal } from './ui';

export interface CardResult { id: string; reference: string; brand: string | null; last4: string | null }

/**
 * The amount goes to the card reader and the customer pays on it. This stays open until the payment is approved, declined or cancelled,
 * and only an approved payment lets the sale go ahead.
 */
export function CardTerminalModal({ amountCents, attemptId, readerId, testMode, onDone }: { amountCents: number; attemptId: string; readerId: string | null; testMode: boolean; onDone: (r: CardResult | null, message?: string) => void }) {
  const [payment, setPayment] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [waited, setWaited] = useState(0);
  const finished = useRef(false);

  const finish = (r: CardResult | null, message?: string) => { if (!finished.current) { finished.current = true; onDone(r, message); } };

  useEffect(() => {
    let stop = false; let timer: ReturnType<typeof setTimeout>;
    (async () => {
      try {
        let p = await api<any>('/api/card/charge', { body: { amountCents, attemptId, readerId } });
        setPayment(p);
        const t0 = Date.now();
        while (!stop && p.status === 'pending') {
          await new Promise((r) => { timer = setTimeout(r, 1500); });
          if (stop) return;
          setWaited(Math.round((Date.now() - t0) / 1000));
          try { p = await api<any>(`/api/card/payments/${p.id}`); setPayment(p); } catch (e) { setErr(e); }   // a hiccup in the connection: keep asking
        }
        if (stop) return;
        if (p.status === 'succeeded') finish({ id: p.id, reference: p.reference, brand: p.brand, last4: p.last4 });
        else if (p.status === 'canceled') finish(null, 'The card payment was cancelled.');
        else setFailed(p.error || 'The card was not accepted.');
      } catch (e) { if (!stop) setFailed(e instanceof Error ? e.message : 'Could not start the card payment.'); }
    })();
    return () => { stop = true; clearTimeout(timer); };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  async function cancel() {
    setErr(null);
    try {
      if (payment) {
        const p = await api<any>(`/api/card/payments/${payment.id}/cancel`, { body: {} });
        if (p.status === 'succeeded') { finish({ id: p.id, reference: p.reference, brand: p.brand, last4: p.last4 }); return; }
      }
      finish(null, 'The card payment was cancelled.');
    } catch (e) { setErr(e); }
  }

  if (failed) return (
    <Modal title="Card not accepted" onClose={() => finish(null, failed)} footer={<button className="primary" onClick={() => finish(null, failed)}>Back to the sale</button>}>
      <div className="notice bad">{failed}</div>
      <p className="faint">Nothing was charged. Try again, or use another way to pay.</p>
    </Modal>
  );
  return (
    <Modal title="Card payment" onClose={() => {}} footer={<button onClick={cancel}>Cancel payment</button>}>
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div className="num" style={{ fontSize: 44, fontWeight: 700 }}>{usd(amountCents)}</div>
        <p style={{ fontSize: 18, margin: '8px 0' }}>{payment ? 'Customer: tap, insert or swipe your card on the reader.' : 'Sending to the card reader…'}</p>
        {waited > 90 && <p className="faint">Still waiting. Check that the reader is on and connected, or cancel.</p>}
      </div>
      <ErrorNote error={err} />
      {testMode && payment && <button onClick={async () => { try { await api(`/api/card/payments/${payment.id}/test-tap`, { body: {} }); } catch (e) { setErr(e); } }}>Test mode: pretend the customer tapped a card</button>}
    </Modal>
  );
}
